import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Clock, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getStudyUsage } from "@/lib/study-monetization.functions";
import type { StudyUsage } from "@/lib/study-policy";
import { remainingDailyQuestions } from "@/lib/study-accounting-policy";
import { QuestionCreditStore } from "./QuestionCreditStore";
import { useAuth } from "@/hooks/useAuth";

export function StudyAllowance({ refreshKey = 0 }: { refreshKey?: number }) {
  const { t } = useTranslation();
  const { questionUsage } = useAuth();
  const [usage, setUsage] = useState<StudyUsage | null>(null);
  const [error, setError] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let disposed = false;
    const load = () => getStudyUsage().then(result => { if (!disposed) { setUsage(result); setError(false); } }).catch(() => { if (!disposed) setError(true); });
    void load();
    const timer = setInterval(load, 60000);
    return () => { disposed = true; clearInterval(timer); };
  }, [refreshKey]);
  useEffect(() => { if (questionUsage) setUsage(questionUsage); }, [questionUsage]);
  // Staged accounting must never replace the working allowance before activation.
  if (!usage?.enforcementEnabled) return error ? <p className="mb-3 text-xs text-ink-muted">{t("monetization.question_allowance_is_temporarily_unavailable")}</p> : null;
  const remaining = remainingDailyQuestions(usage);
  return <section className="mb-4 space-y-3 border-b border-rule pb-4">
    <div className="flex items-center justify-between gap-3 text-sm text-ink">
      <p className="font-semibold"><Sparkles className="mr-2 inline size-4 text-neon-pink" />{t("monetization.ai_questions_remaining_today")}: {remaining === null ? t("monetization.premium_benefits_preserved") : `${remaining} / ${usage.limit}`}</p>
      <span className="shrink-0 text-xs text-ink-muted">{usage.premium ? "Premium" : "Free Forever"}</span>
    </div>
    <p className="flex items-center gap-2 text-xs text-ink-muted"><Clock className="size-3" />Resets at {new Date(usage.resetAt).toLocaleString()} · midnight UTC</p>
    {!usage.premium && <p className="text-xs text-ink-muted">{t("monetization.complimentary_image_scans_remaining")}: {usage.complimentaryScansRemaining} / 1</p>}
    {usage.creditsEnabled && <p className="text-xs text-ink-muted">{usage.credits} credits</p>}
    {!usage.premium && remaining !== null && remaining <= 1 && !dismissed && <div className="flex items-start gap-3">
      <div className="flex-1"><p className="text-sm text-ink">{t("monetization.premium_provides_50_daily_tutor_questions_and_full_study_access")}</p><Link to="/pricing"><Button variant="subtle" className="mt-2">{t("monetization.compare_plans")}</Button></Link></div>
      <Button variant="ghost" size="icon" title={t("monetization.dismiss_offer")}  aria-label={t("monetization.dismiss_upgrade_offer")}  onClick={() => setDismissed(true)}><X /></Button>
    </div>}
    {usage.premium && remaining === 0 && <QuestionCreditStore resetAt={usage.resetAt} exhausted />}
  </section>;
}